import Ajv2020 from "ajv/dist/2020.js";
import type { ErrorObject } from "ajv";
import addFormats from "ajv-formats";
import * as Generated from "./generated.js";

// Import JSON schemas directly for browser compatibility
import controlSchema from "../../schemas/control_message.schema.json" with { type: "json" };
import envelopeSchema from "../../schemas/message_envelope.schema.json" with { type: "json" };
import machineMessageSchema from "../../schemas/machine_message.schema.json" with { type: "json" };
import voiceLaunchRequestSchema from "../../schemas/api_voice_launch_request.schema.json" with { type: "json" };
import voiceLaunchResponseSchema from "../../schemas/api_voice_launch_response.schema.json" with { type: "json" };
import voiceSttRequestSchema from "../../schemas/api_voice_stt_request.schema.json" with { type: "json" };
import voiceSttResponseSchema from "../../schemas/api_voice_stt_response.schema.json" with { type: "json" };
import createUserSessionRequestSchema from "../../schemas/api_sessions_create_request.schema.json" with { type: "json" };
import userSessionSummarySchema from "../../schemas/api_sessions_summary.schema.json" with { type: "json" };
import messagePageResponseSchema from "../../schemas/api_sessions_message_page_response.schema.json" with { type: "json" };
import agentSessionDetailSchema from "../../schemas/api_sessions_agent_detail.schema.json" with { type: "json" };
import createChatMessageRequestSchema from "../../schemas/api_sessions_create_chat_message_request.schema.json" with { type: "json" };
import turnListResponseSchema from "../../schemas/api_sessions_turn_list_response.schema.json" with { type: "json" };
import turnDetailResponseSchema from "../../schemas/api_sessions_turn_detail_response.schema.json" with { type: "json" };
import sessionCostSummaryResponseSchema from "../../schemas/api_sessions_cost_summary_response.schema.json" with { type: "json" };
import usageResponseSchema from "../../schemas/api_billing_usage_response.schema.json" with { type: "json" };
import checkoutResponseSchema from "../../schemas/api_billing_checkout_response.schema.json" with { type: "json" };
import createTopUpRequestSchema from "../../schemas/api_billing_create_topup_request.schema.json" with { type: "json" };
import topUpHistoryResponseSchema from "../../schemas/api_billing_topup_history_response.schema.json" with { type: "json" };
import notificationRequestSchema from "../../schemas/api_settings_notification_request.schema.json" with { type: "json" };
import notificationResponseSchema from "../../schemas/api_settings_notification_response.schema.json" with { type: "json" };
import userSettingsRequestSchema from "../../schemas/api_settings_user_request.schema.json" with { type: "json" };
import userSettingsResponseSchema from "../../schemas/api_settings_user_response.schema.json" with { type: "json" };
import reviewPromptRequestSchema from "../../schemas/api_review_prompt_request.schema.json" with { type: "json" };
import reviewPromptResponseSchema from "../../schemas/api_review_prompt_response.schema.json" with { type: "json" };
import workspaceConfigSchema from "../../schemas/workspace_config.schema.json" with { type: "json" };
import workspaceSessionLaunchRequestSchema from "../../schemas/api_workspaces_session_launch_request.schema.json" with { type: "json" };
import workspaceSessionLaunchResponseSchema from "../../schemas/api_workspaces_session_launch_response.schema.json" with { type: "json" };
import workspaceDescriptorSchema from "../../schemas/api_workspaces_descriptor.schema.json" with { type: "json" };
import machineListResponseSchema from "../../schemas/api_machines_list_response.schema.json" with { type: "json" };
import providerAuthStatusResponseSchema from "../../schemas/api_machines_provider_auth_status_response.schema.json" with { type: "json" };
import attachmentUploadResponseSchema from "../../schemas/api_attachments_upload_response.schema.json" with { type: "json" };
import attachmentsConfig from "../config/attachments.json" with { type: "json" };
import sandboxCreateRequestSchema from "../../schemas/api_sandbox_create_request.schema.json" with { type: "json" };
import sandboxCreateResponseSchema from "../../schemas/api_sandbox_create_response.schema.json" with { type: "json" };
import worktreeDescriptorSchema from "../../schemas/api_worktree_descriptor.schema.json" with { type: "json" };
import worktreeMigrateRequestSchema from "../../schemas/api_worktree_migrate_request.schema.json" with { type: "json" };
import worktreeMigrateResponseSchema from "../../schemas/api_worktree_migrate_response.schema.json" with { type: "json" };
import worktreeRenameRequestSchema from "../../schemas/api_worktree_rename_request.schema.json" with { type: "json" };

const ajv = new Ajv2020({ strict: true, allErrors: true });
addFormats(ajv);

export type ValidationError = ErrorObject<
  string,
  Record<string, unknown>,
  unknown
>;

// Message validators
export const validateControlMessage =
  ajv.compile<Generated.ControlMessage>(controlSchema);
export const validateMessageEnvelope =
  ajv.compile<Generated.MessageEnvelope>(envelopeSchema);
export const validateMachineMessage =
  ajv.compile<Generated.MachineMessage>(machineMessageSchema);
export const validateWorkspaceConfig = ajv.compile<Generated.WorkspaceConfig>(
  workspaceConfigSchema,
);
export const validateWorkspaceSessionLaunchRequest =
  ajv.compile<Generated.WorkspaceSessionLaunchRequest>(
    workspaceSessionLaunchRequestSchema,
  );
export const validateWorkspaceSessionLaunchResponse =
  ajv.compile<Generated.WorkspaceSessionLaunchResponse>(
    workspaceSessionLaunchResponseSchema,
  );
export const validateWorkspaceDescriptor =
  ajv.compile<Generated.WorkspaceDescriptor>(workspaceDescriptorSchema);

// Voice API validators
export const validateVoiceAgentLaunchRequest =
  ajv.compile<Generated.VoiceAgentLaunchRequest>(voiceLaunchRequestSchema);
export const validateVoiceAgentLaunchResponse =
  ajv.compile<Generated.VoiceAgentLaunchResponse>(voiceLaunchResponseSchema);
export const validateVoiceSpeechToTextRequest =
  ajv.compile<Generated.VoiceSpeechToTextRequest>(voiceSttRequestSchema);
export const validateVoiceSpeechToTextResponse =
  ajv.compile<Generated.VoiceSpeechToTextResponse>(voiceSttResponseSchema);

// Sessions API validators
export const validateCreateUserSessionRequest =
  ajv.compile<Generated.CreateUserSessionRequest>(
    createUserSessionRequestSchema,
  );
export const validateUserSessionSummary =
  ajv.compile<Generated.UserSessionSummary>(userSessionSummarySchema);
export const validateMessagePageResponse =
  ajv.compile<Generated.MessagePageResponse>(messagePageResponseSchema);
export const validateAgentSessionDetail =
  ajv.compile<Generated.AgentSessionDetail>(agentSessionDetailSchema);
export const validateCreateChatMessageRequest =
  ajv.compile<Generated.CreateChatMessageRequest>(
    createChatMessageRequestSchema,
  );
export const validateTurnListResponse = ajv.compile<Generated.TurnListResponse>(
  turnListResponseSchema,
);
export const validateTurnDetailResponse =
  ajv.compile<Generated.TurnDetailResponse>(turnDetailResponseSchema);
export const validateSessionCostSummaryResponse =
  ajv.compile<Generated.SessionCostSummaryResponse>(
    sessionCostSummaryResponseSchema,
  );

// Billing API validators
export const validateUsageResponse =
  ajv.compile<Generated.UsageResponse>(usageResponseSchema);
export const validateCheckoutSessionResponse =
  ajv.compile<Generated.CheckoutSessionResponse>(checkoutResponseSchema);
export const validateCreateTopUpRequest =
  ajv.compile<Generated.CreateTopUpRequest>(createTopUpRequestSchema);
export const validateTopUpHistoryResponse =
  ajv.compile<Generated.TopUpHistoryResponse>(topUpHistoryResponseSchema);

// Settings API validators
export const validateUserSettingsUpdateRequest =
  ajv.compile<Generated.UserSettingsUpdateRequest>(userSettingsRequestSchema);
export const validateUserSettingsResponse = ajv.compile<Generated.UserSettings>(
  userSettingsResponseSchema,
);
export const validateUserNotificationSettingsRequest =
  ajv.compile<Generated.UserNotificationSettingsRequest>(
    notificationRequestSchema,
  );
export const validateUserNotificationSettingsResponse =
  ajv.compile<Generated.UserNotificationSettingsResponse>(
    notificationResponseSchema,
  );
export const validateReviewPromptRequest =
  ajv.compile<Generated.ReviewPromptRequest>(reviewPromptRequestSchema);
export const validateReviewPromptResponse =
  ajv.compile<Generated.ReviewPromptResponse>(reviewPromptResponseSchema);

// Machines API validators
export const validateMachineListResponse =
  ajv.compile<Generated.MachineListResponse>(machineListResponseSchema);
export const validateProviderAuthStatusResponse =
  ajv.compile<Generated.ProviderAuthStatusResponse>(
    providerAuthStatusResponseSchema,
  );

// Attachments API validators
export const validateAttachmentUploadResponse =
  ajv.compile<Generated.AttachmentUploadResponse>(
    attachmentUploadResponseSchema,
  );

// Sandbox API validators
export const validateSandboxCreateRequest =
  ajv.compile<Generated.SandboxCreateRequest>(sandboxCreateRequestSchema);
export const validateSandboxCreateResponse =
  ajv.compile<Generated.SandboxCreateResponse>(sandboxCreateResponseSchema);
// Worktree API validators
export const validateWorktreeDescriptor =
  ajv.compile<Generated.WorktreeDescriptor>(worktreeDescriptorSchema);
export const validateWorktreeMigrateRequest =
  ajv.compile<Generated.WorktreeMigrateRequest>(worktreeMigrateRequestSchema);
export const validateWorktreeMigrateResponse =
  ajv.compile<Generated.WorktreeMigrateResponse>(worktreeMigrateResponseSchema);
export const validateWorktreeRenameRequest =
  ajv.compile<Generated.WorktreeRenameRequest>(worktreeRenameRequestSchema);

export type {
  ControlMessageSchema,
  MessageEnvelopeSchema,
  WorkspaceConfigSchema,
  TurnListResponseSchema,
  TurnDetailResponseSchema,
  SessionCostSummaryResponseSchema,
  ControlMessage,
  MessageEnvelope,
  MachineMessage,
  MachineRpcRequest,
  MachineRpcResponse,
  MachineHeartbeatMessage,
  MachineStartSessionPayload,
  MachineStopSessionPayload,
  MachineResumeSessionPayload,
  MachineSetCaffeinatePayload,
  WorkspaceConfig,
  VoiceAgentLaunchRequest,
  VoiceAgentLaunchResponse,
  VoiceSpeechToTextRequest,
  VoiceSpeechToTextResponse,
  CreateUserSessionRequest,
  UserSessionSummary,
  MessagePageResponse,
  AgentSessionDetail,
  CreateChatMessageRequest,
  PromoteQueuedMessageRequest,
  SessionCostSummaryResponse,
  UsageResponse,
  CheckoutSessionResponse,
  CreateTopUpRequest,
  TopUpRecord,
  TopUpHistoryResponse,
  UserSettingsUpdateRequest,
  UserSettings,
  UserNotificationSettingsRequest,
  UserNotificationSettingsResponse,
  ReviewPromptRequest,
  ReviewPromptResponse,
  MachineRegisterRequest,
  MachineRegisterResponse,
  MachineListResponse,
  ProviderAuthAccountInfo,
  ProviderAuthStatusResponse,
  WorkspaceEnsureRequest,
  WorkspaceEnsureRequestSchema,
  WorkspaceDescriptor,
  WorkspaceSessionLaunchRequest,
  WorkspaceSessionLaunchResponse,
  WorkspaceConfigUpdateRequest,
  WorkspaceConfigUpdateRequestSchema,
  AttachmentUploadRequest,
  AttachmentUploadResponse,
  SandboxCreateRequest,
  SandboxCreateResponse,
  WorktreeDescriptor,
  WorktreeMigrateRequest,
  WorktreeMigrateResponse,
  WorktreeRenameRequest,
  TurnCostIngestRequest,
} from "./generated.js";
export type {
  PendingInputView,
  SessionEventRecord,
  TurnEntry,
} from "../collections/agentTurns.js";
export type RawTurnListResponse = Generated.TurnListResponse;
export type RawTurnDetailResponse = Generated.TurnDetailResponse;
export type RawSessionEvent =
  Generated.TurnListResponseSchema.SessionEventSchema;
export type RawSessionInput =
  Generated.TurnListResponseSchema.SessionInputSchema;
export type RawSessionInteraction =
  Generated.TurnListResponseSchema.SessionInteractionSchema;
export type RawTurnSummary = Generated.TurnListResponseSchema.TurnEntry;

// Re-export nested types for convenience
export type MachineDescriptor =
  Generated.MachineListResponseSchema.MachineDescriptor;
export type MachineRestoreCheckpointPayload =
  Generated.MachineMessageSchema.MachineRestoreCheckpointPayload;
// Note: WorktreeDescriptor is now exported directly from generated.ts (from api_worktree_descriptor.schema.json)
// The UserSessionSummary.WorktreeDescriptor should be aligned with the main WorktreeDescriptor type
export type CaffeinateMode =
  Generated.MachineMessageSchema.MachineSetCaffeinatePayload["mode"];
export type GitSettings = Generated.MachineMessageSchema.GitSettings;
export type SyncSettings = Generated.MachineMessageSchema.SyncSettings;
export type UserSessionDescriptor =
  Generated.ControlMessageSchema.UserSessionDescriptor;
export type AgentSessionDescriptor =
  Generated.ControlMessageSchema.AgentSessionDescriptor;
export type ProviderSettings = Generated.UserSettingsSchema.Providers[string];
export type ClaudeCodeSettings = ProviderSettings;
export type CodexSettings = ProviderSettings;

// LanguageMode is validated on backend - frontend just uses string
export type LanguageMode = string;

// Re-export voice language constants from localization
export {
  VOICE_LANGUAGES,
  VOICE_LANGUAGES_BY_TIER,
  type VoiceLanguage,
} from "../localization/voiceLanguages.js";

// Attachment config (source of truth: shared/schemas/data/attachments.json; copied into shared-js/src/config)
type AttachmentsConfig = {
  maxFileSizeBytes: number;
  maxAttachments: number;
  allowedMediaTypes: string[];
};

export const ATTACHMENTS_CONFIG = attachmentsConfig as AttachmentsConfig;
export const MAX_FILE_SIZE_BYTES = ATTACHMENTS_CONFIG.maxFileSizeBytes;
export const MAX_ATTACHMENTS = ATTACHMENTS_CONFIG.maxAttachments;
/** Images must match one of these MIME types (we render/compress only a fixed
 * set). Non-image uploads accept any MIME; the CLI adapter routes arbitrary
 * files through the filesystem path. */
export const IMAGE_MEDIA_TYPES: string[] =
  ATTACHMENTS_CONFIG.allowedMediaTypes.filter((mt) => mt.startsWith("image/"));
